import { isTruthy } from 'corso-types';
import { ReactNode } from 'react';
import { useParams } from 'react-router';
import { match, P } from 'ts-pattern';
import ShippingLine from '~/app/features/crew/order-edit/stage/ShippingLine';
import Alert from '~/components/Alert';
import Badge from '~/components/Badge';
import LinkButton from '~/components/button/LinkButton';
import Submit from '~/components/button/Submit';
import CustomerInfoDisplay from '~/components/CustomerInfoDisplay';
import Form from '~/components/Form';
import Icon from '~/components/Icon';
import Page from '~/components/layout/page/Page';
import LineItem from '~/components/LineItem';
import { useCurrencyFormatter } from '~/utils/formatters';
import { useActionResult, useLoaderData } from './orderEditCommit';

type LoaderData = ReturnType<typeof useLoaderData>;

function DeltaAlert({
  delta,
  direction,
  useStoreCreditRefund,
}: {
  delta: number;
  direction: 'owe' | 'refund' | 'none';
  useStoreCreditRefund: boolean;
}) {
  const formatCurrency = useCurrencyFormatter();

  return match(direction)
    .with('owe', () => (
      <Alert
        variant="info"
        title={`Additional payment of ${formatCurrency(delta)} required`}
      >
        You will be automatically redirected to complete payment. An email will
        be sent to you with details of the change and payment confirmation.
      </Alert>
    ))
    .with('refund', () => (
      <Alert
        variant="info"
        title={`Refund of ${formatCurrency(-delta)} pending`}
      >
        {useStoreCreditRefund
          ? 'The difference will be added to your store credit.'
          : 'The difference will be refunded to your original payment method.'}
      </Alert>
    ))
    .with('none', () => null)
    .exhaustive();
}

function BeforeAfter({
  previousLabel,
  previousNode,
  newLabel,
  newNode,
}: {
  previousLabel: string;
  previousNode: ReactNode;
  newLabel: string;
  newNode: ReactNode;
}) {
  return (
    <div className="flex flex-row items-center justify-around gap-4">
      <div className="flex flex-col gap-4">
        <Badge>{previousLabel}</Badge>
        {previousNode}
      </div>

      <Icon icon="ArrowRight" className="size-5 shrink-0" />

      <div className="flex flex-col gap-4">
        <Badge>{newLabel}</Badge>
        {newNode}
      </div>
    </div>
  );
}

function AddressConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'UPDATE_SHIPPING_ADDRESS' }>;
}) {
  const {
    currentAddress,
    stagedEdits: { updatedShippingAddress },
    action: { editConfirmLabel },
    delta,
    direction,
    useStoreCreditRefund,
  } = data;

  const willRecreate = direction !== 'none';

  return (
    <Form>
      <BeforeAfter
        previousLabel="Previous Address"
        previousNode={
          currentAddress ? (
            <CustomerInfoDisplay
              info={{
                firstName: currentAddress.firstName,
                lastName: currentAddress.lastName,
                address: currentAddress,
              }}
            />
          ) : (
            <p>Address not provided</p>
          )
        }
        newLabel="New Address"
        newNode={
          <CustomerInfoDisplay
            info={{
              firstName: updatedShippingAddress.firstName,
              lastName: updatedShippingAddress.lastName,
              address: updatedShippingAddress,
            }}
          />
        }
      />

      <DeltaAlert
        delta={delta}
        direction={direction}
        useStoreCreditRefund={useStoreCreditRefund}
      />

      {editConfirmLabel && <p>{editConfirmLabel}</p>}
      <Submit>
        {willRecreate ? 'Confirm And Checkout' : 'Update Address'}
      </Submit>
    </Form>
  );
}

function UpdateEmailConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'UPDATE_EMAIL' }>;
}) {
  const {
    currentEmail,
    stagedEdits: { email },
    action: { editConfirmLabel },
  } = data;

  return (
    <Form>
      <BeforeAfter
        previousLabel="Previous Email"
        previousNode={<p>{currentEmail || 'Not provided'}</p>}
        newLabel="New Email"
        newNode={<p>{email}</p>}
      />

      {editConfirmLabel && <p>{editConfirmLabel}</p>}
      <Submit>Update Email</Submit>
    </Form>
  );
}

function CancelConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'CANCEL_ORDER' }>;
}) {
  const {
    action: { editConfirmLabel },
  } = data;

  return (
    <Form>
      {editConfirmLabel && <p>{editConfirmLabel}</p>}
      <Submit> Cancel Order</Submit>
    </Form>
  );
}

function RemovePackageProtectionConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'REMOVE_PACKAGE_PROTECTION' }>;
}) {
  const {
    action: { editConfirmLabel },
  } = data;

  return (
    <Form>
      {editConfirmLabel && <p>{editConfirmLabel}</p>}
      <Submit>Remove Shipping Protection</Submit>
    </Form>
  );
}

function ModifyQuantityConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'MODIFY_QUANTITY' }>;
}) {
  const {
    action: { editConfirmLabel },
    stagedEdits: { lineItemDeltas },
    lineItems,
    delta,
    direction,
  } = data;

  const changedRows = lineItemDeltas
    .map(({ storeOrderLineItemId, newQuantity }) => {
      const lineItem = lineItems.find((li) => li.id === storeOrderLineItemId);
      if (!lineItem) return null;
      const { currentQuantity } = lineItem;
      return { lineItem, currentQuantity, newQuantity };
    })
    .filter(isTruthy);

  const renderRows = (key: 'currentQuantity' | 'newQuantity') => (
    <ul className="flex flex-col gap-3">
      {changedRows.map((row) => (
        <li key={row.lineItem.id}>
          <LineItem
            name={row.lineItem.name ?? ''}
            imgSrc={row.lineItem.imgUrl}
            options={row.lineItem.optionsFromPlatform}
            variant="small"
            quantity={row[key]}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <Form>
      {editConfirmLabel && <p>{editConfirmLabel}</p>}

      <BeforeAfter
        previousLabel="Original"
        previousNode={renderRows('currentQuantity')}
        newLabel="Updated"
        newNode={renderRows('newQuantity')}
      />

      <DeltaAlert
        delta={delta}
        direction={direction}
        useStoreCreditRefund={data.useStoreCreditRefund}
      />
      <Submit>
        {direction === 'owe' ? 'Confirm And Checkout' : 'Confirm'}
      </Submit>
    </Form>
  );
}

function UpdateShippingLineConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'UPDATE_SHIPPING_LINE' }>;
}) {
  const {
    action: { editConfirmLabel },
    stagedEdits: { selectedShippingLine },
    originalShippingTitle,
    originalShippingPrice,
    delta,
    direction,
  } = data;
  const formatCurrency = useCurrencyFormatter();

  return (
    <Form>
      {editConfirmLabel && <p>{editConfirmLabel}</p>}

      <BeforeAfter
        newLabel="New Shipping Method"
        newNode={
          <ShippingLine
            price={formatCurrency(
              Number.parseFloat(selectedShippingLine.price),
            )}
            title={selectedShippingLine.title}
          />
        }
        previousLabel="Current Shipping Method"
        previousNode={
          <ShippingLine
            price={
              originalShippingPrice
                ? formatCurrency(Number.parseFloat(originalShippingPrice))
                : undefined
            }
            title={originalShippingTitle ?? 'No Shipping Title'}
          />
        }
      />

      <DeltaAlert
        delta={delta}
        direction={direction}
        useStoreCreditRefund={data.useStoreCreditRefund}
      />
      <Submit>
        {direction === 'owe' ? 'Confirm And Checkout' : 'Confirm'}
      </Submit>
    </Form>
  );
}

function AddDiscountCodeConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'ADD_DISCOUNT_CODE' }>;
}) {
  const {
    action: { editConfirmLabel },
    stagedEdits: { code, originalTotal, calculatedTotal },

    delta,
    direction,
  } = data;
  const formatCurrency = useCurrencyFormatter();

  return (
    <Form>
      {editConfirmLabel && <p>{editConfirmLabel}</p>}
      <BeforeAfter
        previousLabel="Original Total"
        previousNode={
          <p>{formatCurrency(Number.parseFloat(originalTotal.amount))}</p>
        }
        newLabel="New Total"
        newNode={
          <p>{formatCurrency(Number.parseFloat(calculatedTotal.amount))}</p>
        }
      />
      <p>Discount code &ldquo;{code}&rdquo; applied.</p>
      <DeltaAlert
        delta={delta}
        direction={direction}
        useStoreCreditRefund={data.useStoreCreditRefund}
      />
      <Submit>Confirm</Submit>
    </Form>
  );
}

function SharedResult({ ok, message }: { ok: boolean; message: string }) {
  const params = useParams();

  return (
    <>
      <Alert
        title={ok ? 'Success' : 'Something went wrong'}
        variant={ok ? 'success' : 'error'}
      >
        {message}
      </Alert>
      <LinkButton
        relative="path"
        to={`/${params.store}/order/${params.idFromPlatform}`}
        className="w-full shrink-0 md:w-60"
      >
        Go Home
      </LinkButton>
    </>
  );
}

export default function OrderEditCommitPage() {
  const result = useActionResult();
  const loaderData = useLoaderData();

  const confirmContent = match(loaderData)
    .with({ editType: 'UPDATE_SHIPPING_ADDRESS' }, (data) => (
      <AddressConfirm data={data} />
    ))
    .with({ editType: 'CANCEL_ORDER' }, (data) => <CancelConfirm data={data} />)
    .with({ editType: 'REMOVE_PACKAGE_PROTECTION' }, (data) => (
      <RemovePackageProtectionConfirm data={data} />
    ))
    .with({ editType: 'MODIFY_QUANTITY' }, (data) => (
      <ModifyQuantityConfirm data={data} />
    ))
    .with({ editType: 'UPDATE_EMAIL' }, (data) => (
      <UpdateEmailConfirm data={data} />
    ))
    .with({ editType: 'UPDATE_SHIPPING_LINE' }, (data) => (
      <UpdateShippingLineConfirm data={data} />
    ))
    .with({ editType: 'ADD_DISCOUNT_CODE' }, (data) => (
      <AddDiscountCodeConfirm data={data} />
    ))
    .exhaustive();

  return (
    <Page.Root title={loaderData.headline} hideHeader>
      <Page.Section>
        {match(result)
          .with(P.nullish, () => confirmContent)
          .otherwise((r) => (
            <SharedResult ok={r.ok} message={r.message} />
          ))}
      </Page.Section>
    </Page.Root>
  );
}
